import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Fingerprint, ShieldCheck, KeyRound, Copy, Eye, EyeOff, Lock, RefreshCw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dual-cosign password manager for the "Linda is the boss. Kwai Yuk Mak is
 * the boss." payout button. Only visible to `linda8mak@gmail.com` and the
 * platform owner. Lets the operator:
 *   • create the initial password for each of Linda's two names
 *   • change either password (requires the current one)
 *   • recover using the one-time recovery code shown at creation
 * Recovery codes are single-use — recovering rotates the code.
 */
const DualCosignManager = ({ token, userEmail }) => {
  const ALLOWED = new Set(["linda8mak@gmail.com", "info@stardomfans.com"]);
  const allowed = ALLOWED.has((userEmail || "").toLowerCase());

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // { mode: 'set'|'change'|'recover', name }
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ new_password: "", confirm: "", old_password: "", recovery_code: "" });
  const [showPw, setShowPw] = useState(false);
  const [issuedRecovery, setIssuedRecovery] = useState(null); // { name, recovery_code }

  const load = useCallback(async () => {
    if (!allowed || !token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/cosign/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(data);
    } catch (e) {
      // Non-admin gets 403 — silently hide.
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [allowed, token]);

  useEffect(() => { load(); }, [load]);

  if (!allowed) return null;
  if (loading) return null;

  const openModal = (mode, name) => {
    setForm({ new_password: "", confirm: "", old_password: "", recovery_code: "" });
    setShowPw(false);
    setIssuedRecovery(null);
    setModal({ mode, name });
  };

  const submit = async () => {
    if (!modal) return;
    if (!form.new_password || form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters"); return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Passwords do not match"); return;
    }
    setBusy(true);
    try {
      if (modal.mode === "set") {
        const { data } = await axios.post(
          `${API}/admin/cosign/set`,
          { name: modal.name, new_password: form.new_password },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setIssuedRecovery({ name: modal.name, recovery_code: data.recovery_code });
        toast.success("Password created — save your recovery code!");
      } else if (modal.mode === "change") {
        if (!form.old_password) { toast.error("Enter the current password"); setBusy(false); return; }
        await axios.post(
          `${API}/admin/cosign/change`,
          { name: modal.name, old_password: form.old_password, new_password: form.new_password },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("Password changed");
        setModal(null);
      } else if (modal.mode === "recover") {
        if (!form.recovery_code) { toast.error("Enter the recovery code"); setBusy(false); return; }
        const { data } = await axios.post(
          `${API}/admin/cosign/recover`,
          { name: modal.name, recovery_code: form.recovery_code, new_password: form.new_password },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setIssuedRecovery({ name: modal.name, recovery_code: data.recovery_code });
        toast.success("Password recovered — save your NEW recovery code!");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const NAMES = [
    { key: "linda", label: "Linda" },
    { key: "kwai_yuk_mak", label: "Kwai Yuk Mak" },
  ];

  return (
    <section className="rounded-xl border border-[#EC008C]/30 bg-gradient-to-br from-[#EC008C]/[0.06] to-[#0A0E18] p-4 sm:p-5" data-testid="dual-cosign-manager">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 flex-wrap text-left"
        data-testid="dual-cosign-toggle"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#EC008C]/15 border border-[#EC008C]/40 flex items-center justify-center flex-shrink-0">
            <Fingerprint className="w-5 h-5 text-[#EC008C]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Dual-Cosign Passwords</p>
            <h2 className="text-white font-bold">Linda &amp; Kwai Yuk Mak</h2>
            <p className="text-[11px] text-[#8A92A0] mt-0.5">
              Both passwords are required to press the &quot;Linda is the boss.&quot; payout button.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {NAMES.map((n) => (
            <span
              key={n.key}
              className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${
                status?.names?.[n.key]?.is_set
                  ? "bg-[#8DB87A]/15 text-[#8DB87A] border border-[#8DB87A]/40"
                  : "bg-[#EBB84C]/15 text-[#EBB84C] border border-[#EBB84C]/40"
              }`}
              data-testid={`cosign-status-${n.key}`}
            >
              {n.label}: {status?.names?.[n.key]?.is_set ? "SET" : "NOT SET"}
            </span>
          ))}
          <ShieldCheck className={`w-4 h-4 ${status?.both_set ? "text-[#8DB87A]" : "text-[#EBB84C]"}`} />
        </div>
      </button>

      {open && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dual-cosign-body">
          {NAMES.map((n) => {
            const set = !!status?.names?.[n.key]?.is_set;
            return (
              <div key={n.key} className="rounded-lg border border-[#1A2030] bg-[#050810] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-[#EC008C]" />
                  <p className="text-white font-bold">{n.label}</p>
                </div>
                <p className="text-[11px] text-[#8A92A0] mb-3">
                  {set
                    ? `Password set · updated ${status.names[n.key].updated_at ? new Date(status.names[n.key].updated_at).toLocaleDateString() : ""}`
                    : "No password yet — create one to enable the dual-cosign button."}
                </p>
                <div className="flex flex-wrap gap-2">
                  {!set && (
                    <button
                      onClick={() => openModal("set", n.key)}
                      className="h-8 px-3 rounded-full bg-[#EC008C] text-black text-xs font-bold flex items-center gap-1 hover:bg-[#FF3399]"
                      data-testid={`cosign-set-btn-${n.key}`}
                    >
                      <KeyRound className="w-3 h-3" /> Create password
                    </button>
                  )}
                  {set && (
                    <>
                      <button
                        onClick={() => openModal("change", n.key)}
                        className="h-8 px-3 rounded-full border border-[#EC008C]/40 text-[#EC008C] text-xs font-bold flex items-center gap-1 hover:bg-[#EC008C]/10"
                        data-testid={`cosign-change-btn-${n.key}`}
                      >
                        <RefreshCw className="w-3 h-3" /> Change
                      </button>
                      <button
                        onClick={() => openModal("recover", n.key)}
                        className="h-8 px-3 rounded-full border border-[#1A2030] text-[#8A92A0] text-xs font-bold flex items-center gap-1 hover:text-white"
                        data-testid={`cosign-recover-btn-${n.key}`}
                      >
                        <KeyRound className="w-3 h-3" /> Recover
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" data-testid="cosign-modal" onClick={() => !busy && setModal(null)}>
          <div className="bg-[#0A0E18] border border-[#EC008C]/40 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Unbounded'] text-lg font-bold text-white">
                {modal.mode === "set" && "Create password"}
                {modal.mode === "change" && "Change password"}
                {modal.mode === "recover" && "Recover password"}
              </h3>
              <button onClick={() => setModal(null)} className="text-[#8A92A0] hover:text-white text-lg">✕</button>
            </div>
            <p className="text-xs text-[#8A92A0] mb-4">For <span className="font-bold text-white">{modal.name === "linda" ? "Linda" : "Kwai Yuk Mak"}</span></p>

            {issuedRecovery ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-[#F0C464]/40 bg-[#F0C464]/10 p-4">
                  <p className="text-xs text-[#F0C464] uppercase tracking-widest mb-2">One-time recovery code · SAVE NOW</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 font-mono text-white bg-black/50 rounded px-3 py-2 text-sm select-all" data-testid="cosign-recovery-code">
                      {issuedRecovery.recovery_code}
                    </code>
                    <button
                      onClick={() => { navigator.clipboard.writeText(issuedRecovery.recovery_code); toast.success("Copied"); }}
                      className="h-9 w-9 rounded-lg bg-[#F0C464] text-black flex items-center justify-center hover:bg-[#EBB84C]"
                      data-testid="cosign-copy-recovery"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-[11px] text-[#8A92A0] mt-2">This code is shown ONCE. Store it in your password manager. Use it later to recover this name&apos;s password if forgotten.</p>
                </div>
                <button
                  onClick={() => { setModal(null); setIssuedRecovery(null); }}
                  className="w-full h-10 rounded-full bg-[#EC008C] text-black font-bold text-sm"
                  data-testid="cosign-done-btn"
                >
                  I saved it — Done
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {modal.mode === "change" && (
                  <div>
                    <label className="text-xs text-[#8A92A0]">Current password</label>
                    <input
                      type={showPw ? "text" : "password"}
                      value={form.old_password}
                      onChange={(e) => setForm({ ...form, old_password: e.target.value })}
                      className="w-full mt-1 bg-black/40 border border-[#1A2030] rounded-lg px-3 py-2 text-white text-sm"
                      data-testid="cosign-old-password"
                    />
                  </div>
                )}
                {modal.mode === "recover" && (
                  <div>
                    <label className="text-xs text-[#8A92A0]">Recovery code</label>
                    <input
                      type="text"
                      value={form.recovery_code}
                      onChange={(e) => setForm({ ...form, recovery_code: e.target.value.toUpperCase() })}
                      placeholder="XXXX-XXXX-XXXX-XXXX"
                      className="w-full mt-1 bg-black/40 border border-[#1A2030] rounded-lg px-3 py-2 text-white font-mono text-sm tracking-wider"
                      data-testid="cosign-recovery-input"
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs text-[#8A92A0]">New password (min 6 chars)</label>
                  <div className="relative mt-1">
                    <input
                      type={showPw ? "text" : "password"}
                      value={form.new_password}
                      onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                      className="w-full bg-black/40 border border-[#1A2030] rounded-lg px-3 py-2 pr-10 text-white text-sm"
                      data-testid="cosign-new-password"
                    />
                    <button onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8A92A0] hover:text-white">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#8A92A0]">Confirm new password</label>
                  <input
                    type={showPw ? "text" : "password"}
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    className="w-full mt-1 bg-black/40 border border-[#1A2030] rounded-lg px-3 py-2 text-white text-sm"
                    data-testid="cosign-confirm-password"
                  />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={submit}
                    disabled={busy}
                    className="flex-1 h-10 rounded-full bg-[#EC008C] text-black font-bold text-sm hover:bg-[#FF3399] disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="cosign-submit-btn"
                  >
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save"}
                  </button>
                  <button
                    onClick={() => setModal(null)}
                    className="h-10 px-4 rounded-full border border-[#1A2030] text-[#8A92A0] text-sm"
                    data-testid="cosign-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default DualCosignManager;
